import React, { useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend, ReferenceLine,
} from "recharts";
import { Flag, Info } from "lucide-react";

const fmtUSD = (n) => (isFinite(n) ? `$${Math.round(n).toLocaleString()}` : "—");
const fmtAxis = (n) => {
  if (!isFinite(n)) return "";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${Math.round(n)}`;
};

function Stat({ label, value, sub, color, testid }) {
  return (
    <div className="glass p-4 fade-up" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-mono text-ink-3 mb-1">{label}</div>
      <div className="font-display text-2xl md:text-3xl font-semibold tracking-tight" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px] text-ink-3 mt-0.5">{sub}</div>}
    </div>
  );
}

/** When do you get your money back — and what does it cost you? */
export default function RecoveryView({ principal, dailyRate, days, milestone }) {
  const sim = useMemo(() => {
    const P = Number(principal) || 0;
    const r = (Number(dailyRate) || 0) / 100;
    const T = Math.min(1080, Math.max(1, Math.round(Number(days) || 0)));
    const M = Number(milestone) || 0;
    let a = P, b = P, c = P, cash = 0, cCash = 0, cBase = null, recoveryDay = null;
    const rows = [{ day: 0, a: P, b: P, cash: 0, bTotal: P, cCash: 0, cTotal: P }];
    for (let d = 1; d <= T; d++) {
      a *= 1 + r; b *= 1 + r; c *= 1 + r;
      if (recoveryDay === null && M > 0 && b - P >= M) {
        recoveryDay = d;
        b -= M; cash = M; c -= M; cCash = M; cBase = c;
      }
      if (recoveryDay !== null && d > recoveryDay && (d - recoveryDay) % 30 === 0) {
        const inc = c - cBase;
        if (inc > 0) { cCash += inc; c = cBase; }
      }
      rows.push({ day: d, a: Math.round(a), b: Math.round(b), cash, bTotal: Math.round(b + cash), cCash: Math.round(cCash), cTotal: Math.round(c + cCash) });
    }
    const monthlyIncome = cBase ? cBase * (Math.pow(1 + r, 30) - 1) : 0;
    return { rows, recoveryDay, aFinal: a, bFinal: b, cash, bTotal: b + cash, cFinal: c, cCash, cTotal: c + cCash, cBase, monthlyIncome, P, T };
  }, [principal, dailyRate, days, milestone]);

  const tableRows = useMemo(() => {
    const set = new Set([0]);
    for (let d = 30; d <= sim.T; d += 30) set.add(d);
    set.add(sim.T);
    if (sim.recoveryDay) set.add(sim.recoveryDay);
    return [...set].sort((x, y) => x - y).map((d) => sim.rows[d]).filter(Boolean);
  }, [sim]);

  return (
    <div className="space-y-5" data-testid="recovery-view">
      <p className="text-sm text-ink-2" data-testid="crs-explainer">
        Three identical stakes, three strategies. <b>A</b> compounds untouched for the full term.
        <b> B</b> compounds until profits equal your withdrawal target, pulls that cash out — your capital is back
        in your pocket — then lets the rest ride. <b>C</b> recovers capital the same way, then harvests the
        profits every 30 days as monthly income while the staked base keeps working.
      </p>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <Stat label="Capital recovered" value={sim.recoveryDay ? `Day ${sim.recoveryDay}` : "—"}
          sub={sim.recoveryDay ? `profits hit ${fmtUSD(Number(milestone) || 0)} · break-even` : "not within term"}
          color="#00863A" testid="crs-breakeven" />
        <Stat label="Scenario A · let it all ride" value={fmtUSD(sim.aFinal)}
          sub={`${fmtUSD(sim.aFinal - sim.P)} profit · capital at risk all ${sim.T} days`}
          color="#4060FF" testid="crs-a-total" />
        <Stat label="Scenario B · recover, then ride" value={fmtUSD(sim.bTotal)}
          sub={sim.recoveryDay ? `${fmtUSD(sim.cash)} cash in pocket + ${fmtUSD(sim.bFinal)} still staked` : "same as Scenario A"}
          color="#EC4899" testid="crs-b-total" />
        <Stat label="Scenario C · monthly income" value={fmtUSD(sim.cTotal)}
          sub={sim.recoveryDay ? `≈ ${fmtUSD(sim.monthlyIncome)}/mo income after day ${sim.recoveryDay} · ${fmtUSD(sim.cBase)} stays staked` : "same as Scenario A"}
          color="#F59E0B" testid="crs-c-total" />
      </div>

      <div className="glass p-5 fade-up" data-testid="crs-chart">
        <div className="text-[10px] uppercase tracking-wider font-mono text-ink-3">Side by side</div>
        <h3 className="font-display text-lg font-semibold tracking-tight mb-3">Growth of all three scenarios</h3>
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <LineChart data={sim.rows}>
              <CartesianGrid stroke="rgba(15,18,38,0.06)" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={fmtAxis} width={70} />
              <RTooltip
                contentStyle={{ background: "rgba(255,255,255,0.96)", border: "1px solid rgba(15,18,38,0.10)", borderRadius: 10, fontSize: 12 }}
                formatter={(v, n) => [fmtUSD(v), n]}
                labelFormatter={(d) => `Day ${d}`}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {sim.recoveryDay && (
                <ReferenceLine x={sim.recoveryDay} stroke="#00863A" strokeDasharray="4 4"
                  label={{ value: `Capital back · day ${sim.recoveryDay}`, position: "top", fill: "#00863A", fontSize: 11, fontWeight: 700 }} />
              )}
              <Line type="monotone" dataKey="a" name="A · all-in balance" stroke="#4060FF" strokeWidth={2.4} dot={false} />
              <Line type="monotone" dataKey="b" name="B · still staked" stroke="#EC4899" strokeWidth={2.2} dot={false} />
              <Line type="monotone" dataKey="bTotal" name="B · total incl. cash out" stroke="#00863A" strokeWidth={2} strokeDasharray="6 4" dot={false} />
              <Line type="monotone" dataKey="cTotal" name="C · total incl. monthly income" stroke="#F59E0B" strokeWidth={2} strokeDasharray="2 4" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="glass p-5 fade-up" data-testid="crs-table-card">
        <div className="text-[10px] uppercase tracking-wider font-mono text-ink-3">Milestones</div>
        <h3 className="font-display text-lg font-semibold tracking-tight mb-3">The numbers, month by month</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="crs-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider font-mono text-ink-3 border-b" style={{ borderColor: "rgba(15,18,38,0.08)" }}>
                <th className="text-left py-2">Day</th>
                <th className="text-right py-2">A · all-in</th>
                <th className="text-right py-2">B · staked</th>
                <th className="text-right py-2">B · cash out</th>
                <th className="text-right py-2">B · total</th>
                <th className="text-right py-2">C · cash</th>
                <th className="text-right py-2">C · total</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((r) => {
                const isRec = r.day === sim.recoveryDay;
                return (
                  <tr key={r.day} className="border-b" style={{ borderColor: "rgba(15,18,38,0.04)", background: isRec ? "rgba(0,134,58,0.06)" : "transparent" }} data-testid={`crs-row-${r.day}`}>
                    <td className="py-2 font-mono font-semibold">{r.day}{isRec && <span className="ml-1.5 text-[10px] font-bold" style={{ color: "#00863A" }}><Flag size={10} className="inline -mt-0.5" /> capital back</span>}</td>
                    <td className="py-2 text-right font-mono tabular-nums">{fmtUSD(r.a)}</td>
                    <td className="py-2 text-right font-mono tabular-nums">{fmtUSD(r.b)}</td>
                    <td className="py-2 text-right font-mono tabular-nums" style={{ color: r.cash ? "#00863A" : undefined }}>{r.cash ? fmtUSD(r.cash) : "—"}</td>
                    <td className="py-2 text-right font-mono tabular-nums font-semibold">{fmtUSD(r.bTotal)}</td>
                    <td className="py-2 text-right font-mono tabular-nums" style={{ color: r.cCash ? "#F59E0B" : undefined }}>{r.cCash ? fmtUSD(r.cCash) : "—"}</td>
                    <td className="py-2 text-right font-mono tabular-nums font-semibold">{fmtUSD(r.cTotal)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-ink-3 mt-3 flex items-start gap-1.5">
          <Info size={12} className="shrink-0 mt-0.5" />
          Illustration only: assumes earnings compound daily at a constant rate and profits can be withdrawn at the milestone.
          Real staking terms, reward rules and CA price moves will change these numbers. Not financial advice.
        </p>
      </div>
    </div>
  );
}
